import React, { useState } from 'react';
import { useUsers } from '../hooks/useUsers';
import { useGroups } from '../hooks/useGroups';
import LoadingSpinner from '../shared/LoadingSpinner';
import ErrorAlert from '../shared/ErrorAlert';
import SearchBar from '../shared/SearchBar';
import Pagination from '../shared/Pagination';
import UserTable from './UserTable';
import CreateUserModal from './CreateUserModal';
import UserSearchModal from './UserSearchModal';
import AddUserToGroupModal from './AddUserToGroupModal';
import './UserManagement.css';

/**
 * Main User Management Component
 * Handles all user-related operations: list, create, search, assign to groups
 */
const UserManagement = () => {
  const {
    users,
    loading: usersLoading,
    error: usersError,
    pagination,
    createUser,
    findUserByEmail,
    findUserById,
    filterUsers,
    nextPage,
    prevPage,
    goToPage,
    changePageSize,
    refresh: refreshUsers
  } = useUsers();

  const {
    groups,
    loading: groupsLoading,
    addUserToGroup,
    refresh: refreshGroups
  } = useGroups();

  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showAddToGroupModal, setShowAddToGroupModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  // Filter users based on search term
  const filteredUsers = filterUsers(searchTerm);

  // Calculate pagination for filtered results
  const totalFilteredUsers = filteredUsers.length;
  const startIndex = (pagination.currentPage - 1) * pagination.pageSize;
  const endIndex = startIndex + pagination.pageSize;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);
  const totalPages = Math.ceil(totalFilteredUsers / pagination.pageSize);

  // Handle user creation
  const handleCreateUser = async (userData) => {
    setActionLoading(true);
    setActionError(null);
    
    try {
      const result = await createUser(userData);
      
      if (result.success) {
        setSuccessMessage(result.message);
        setShowCreateModal(false);
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setActionError(result.error);
      }
    } catch (error) {
      setActionError(error.message || 'Failed to create user');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle user search
  const handleUserSearch = async (searchData) => {
    setActionLoading(true);
    setActionError(null);
    
    try {
      let result;
      if (searchData.type === 'email') {
        result = await findUserByEmail(searchData.value);
      } else {
        result = await findUserById(searchData.value);
      }
      
      return result;
    } catch (error) {
      setActionError(error.message || 'Search failed');
      return { success: false, error: error.message };
    } finally {
      setActionLoading(false);
    }
  };

  // Handle adding user to group
  const handleAddUserToGroup = async (userUID, groupName) => {
    setActionLoading(true);
    setActionError(null);
    
    try {
      const result = await addUserToGroup(groupName, userUID);
      
      if (result.success) {
        setSuccessMessage(result.message);
        setShowAddToGroupModal(false);
        refreshUsers();
        refreshGroups();
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setActionError(result.error);
      }
    } catch (error) {
      setActionError(error.message || 'Failed to add user to group');
    } finally {
      setActionLoading(false);
    }
  };

  // Clear error messages
  const clearError = () => {
    setActionError(null);
  };

  // Clear success messages
  const clearSuccess = () => {
    setSuccessMessage('');
  };

  return (
    <div className="user-management">
      <div className="page-header">
        <h2>User Management</h2>
        <div className="header-actions">
          <button 
            className="btn btn-primary"
            onClick={() => setShowCreateModal(true)}
            disabled={usersLoading || actionLoading}
          >
            <i className="fas fa-plus"></i> Create User
          </button>
          <button 
            className="btn btn-secondary"
            onClick={() => setShowSearchModal(true)}
            disabled={usersLoading || actionLoading}
          >
            <i className="fas fa-search"></i> Search Users
          </button>
          <button 
            className="btn btn-outline-primary"
            onClick={refreshUsers}
            disabled={usersLoading || actionLoading}
          >
            <i className="fas fa-refresh"></i> Refresh
          </button>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="alert alert-success alert-dismissible" role="alert">
          {successMessage}
          <button 
            type="button" 
            className="btn-close" 
            onClick={clearSuccess}
          >
            <span>&times;</span>
          </button>
        </div>
      )}

      {/* Error Alert */}
      <ErrorAlert 
        error={usersError || actionError} 
        onDismiss={clearError} 
      />

      {/* Search Bar */}
      <div className="search-section">
        <SearchBar
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search users by name, email, or UID..."
          disabled={usersLoading || actionLoading}
          onClear={() => setSearchTerm('')}
        />
      </div>

      {/* User Statistics */}
      <div className="user-stats">
        <div className="stats-card">
          <div className="stat-item">
            <span className="stat-label">Total Users:</span>
            <span className="stat-value">{users.length}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Filtered Results:</span>
            <span className="stat-value">{totalFilteredUsers}</span>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {usersLoading && (
        <LoadingSpinner message="Loading users..." />
      )}

      {/* Users Table */}
      {!usersLoading && (
        <>
          <UserTable 
            users={paginatedUsers}
            onAddToGroup={(user) => {
              setSelectedUser(user);
              setShowAddToGroupModal(true);
            }}
            loading={actionLoading}
          />
          
          {/* Pagination */}
          <Pagination
            currentPage={pagination.currentPage}
            totalPages={totalPages}
            onPageChange={goToPage}
            pageSize={pagination.pageSize}
            onPageSizeChange={changePageSize}
            totalItems={totalFilteredUsers}
          />
        </>
      )}

      {/* Modals */}
      <CreateUserModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onSubmit={handleCreateUser}
        groups={groups}
        loading={actionLoading}
      />

      <UserSearchModal
        show={showSearchModal}
        onHide={() => setShowSearchModal(false)}
        onSearch={handleUserSearch}
        loading={actionLoading}
      />

      <AddUserToGroupModal
        show={showAddToGroupModal}
        onHide={() => setShowAddToGroupModal(false)}
        user={selectedUser}
        groups={groups}
        onSubmit={handleAddUserToGroup}
        loading={actionLoading}
      />
    </div>
  );
};

export default UserManagement;
